import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Gift, CreditCard, ArrowLeft, MessageCircle } from "lucide-react";
import { BrandLogo, BRAND } from "@/components/Brand";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSignup() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const [pixEnabled, setPixEnabled] = useState(false);
  const [supportWhatsapp, setSupportWhatsapp] = useState("");
  const [form, setForm] = useState(() => {
    // Restaura form ao voltar do checkout — evita re-digitar tudo.
    try {
      const cached = sessionStorage.getItem("vendiz_signup_form");
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return {
      tenant_name: "", owner_name: "", owner_email: "", owner_phone: "",
      password: "", password_confirm: "",
      plan: "", payment_mode: "trial", trial_days: 7,
    };
  });
  const upd = (k, v) => {
    setForm(p => {
      const nxt = { ...p, [k]: v };
      try { sessionStorage.setItem("vendiz_signup_form", JSON.stringify(nxt)); } catch { /* ignore */ }
      return nxt;
    });
  };

  useEffect(() => {
    axios.get(`${API}/public/checkout-plans`).then(r => {
      const list = r.data?.plans || [];
      setPlans(list);
      setPixEnabled(!!r.data?.pix_enabled);
      setSupportWhatsapp(r.data?.support_whatsapp || "");
      if (list.length) setForm(p => ({ ...p, plan: p.plan || list[0].key }));
    }).catch(() => {
      const fb = [{ key: "Start", label: "Start", price: 49.90, features: [] }];
      setPlans(fb); setForm(p => ({ ...p, plan: "Start" }));
    });
  }, []);

  const validate = () => {
    if (!form.tenant_name.trim()) return "Informe o nome da sua loja";
    if (!form.owner_name.trim()) return "Informe seu nome";
    if (!form.owner_email.includes("@") || !form.owner_email.includes(".")) return "E-mail inválido";
    if (!form.owner_phone.replace(/\D/g, "")) return "Informe seu WhatsApp";
    if (form.password.length < 8) return "Senha precisa ter ao menos 8 caracteres";
    if (form.password !== form.password_confirm) return "As senhas não conferem";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const payload = { ...form, origin_url: window.location.origin };
      delete payload.password_confirm;
      const r = await axios.post(`${API}/public/register`, payload);
      if (r.data?.checkout_url) {
        // Antes de sair, garante cache do form pra caso o usuário volte.
        try { sessionStorage.setItem("vendiz_signup_form", JSON.stringify(form)); } catch { /* ignore */ }
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success("Cadastro realizado! Seu teste de 7 dias começou.");
      try { sessionStorage.removeItem("vendiz_signup_form"); } catch { /* ignore */ }
      setTimeout(() => nav("/login"), 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erro ao cadastrar";
      if (String(detail).toLowerCase().includes("já cadastrado") || String(detail).toLowerCase().includes("em uso")) {
        toast.error("Este e-mail já tem cadastro. Faça login ou use outro e-mail.");
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const selectedPlan = plans.find(p => p.key === form.plan) || plans[0] || { label: "—", price: 0 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white flex items-center justify-center p-4 relative overflow-hidden">
      {/* Blobs animados de fundo — sofistificação visual sem quebrar performance */}
      <div className="absolute -top-40 -left-40 w-[420px] h-[420px] rounded-full bg-emerald-500/20 blur-3xl animate-pulse pointer-events-none"/>
      <div className="absolute -bottom-40 -right-40 w-[420px] h-[420px] rounded-full bg-indigo-500/20 blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: "1s" }}/>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.05),transparent_60%)] pointer-events-none"/>

      <Card
        className="w-full max-w-lg p-7 border-white/10 bg-neutral-900/70 backdrop-blur-xl shadow-2xl relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500 [&_label]:text-neutral-200 [&_input]:bg-neutral-800/70 [&_input]:border-white/10 [&_input]:text-white [&_input::placeholder]:text-neutral-500"
        data-testid="public-signup-card"
      >
        <div className="text-center mb-5">
          <div className="flex justify-center mb-3">
            <img
              src={BRAND.logo}
              alt="Gestão Expert"
              className="w-16 h-16 object-contain drop-shadow-[0_0_28px_rgba(16,185,129,0.35)] animate-in zoom-in duration-500"
            />
          </div>
          <h1 className="font-display text-3xl bg-clip-text text-transparent bg-gradient-to-b from-white to-neutral-300">Crie sua conta</h1>
          <p className="text-xs text-neutral-400 mt-2">Ative seu <strong className="text-emerald-400">teste grátis de 7 dias</strong> ou assine agora com cartão{pixEnabled ? "/PIX" : ""}.</p>
        </div>

        {step === 1 && (
          <div className="space-y-3" data-testid="signup-step-1">
            <div>
              <Label>Nome da loja</Label>
              <Input value={form.tenant_name} onChange={e=>upd("tenant_name", e.target.value)} placeholder="Ex: Import Store Brasil" data-testid="signup-tenant-name" required/>
            </div>
            <div>
              <Label>Seu nome</Label>
              <Input value={form.owner_name} onChange={e=>upd("owner_name", e.target.value)} data-testid="signup-owner-name" required/>
            </div>
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={form.owner_email} onChange={e=>upd("owner_email", e.target.value)} data-testid="signup-email" required/>
            </div>
            <div>
              <Label>WhatsApp (com DDD)</Label>
              <Input value={form.owner_phone} onChange={e=>upd("owner_phone", e.target.value)} placeholder="11999998888" data-testid="signup-phone" required/>
            </div>
            <div>
              <Label>Senha (mín. 8 caracteres)</Label>
              <Input type="password" value={form.password} onChange={e=>upd("password", e.target.value)} data-testid="signup-password" required minLength={8}/>
            </div>
            <div>
              <Label>Confirmar senha</Label>
              <Input type="password" value={form.password_confirm} onChange={e=>upd("password_confirm", e.target.value)} data-testid="signup-password-confirm" required minLength={8}/>
              {form.password && form.password_confirm && form.password !== form.password_confirm && (
                <p className="text-[11px] text-red-600 mt-1">As senhas não conferem</p>
              )}
            </div>
            <Button onClick={()=>{
              const err = validate();
              if (err) return toast.error(err);
              setStep(2);
            }} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="signup-next-btn">Continuar</Button>
            <p className="text-[11px] text-center text-neutral-400">Já tem conta? <a href="/login" className="underline text-emerald-300 hover:text-emerald-200">Entrar</a></p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="signup-step-2">
            <div>
              <Label>Plano</Label>
              <div className={`grid ${plans.length >= 2 ? "grid-cols-2" : "grid-cols-1"} gap-2 mt-2`}>
                {plans.map(p => (
                  <button
                    key={p.key} type="button" onClick={()=>upd("plan", p.key)}
                    className={`p-3 rounded-lg border text-left transition ${form.plan === p.key ? "border-emerald-400 bg-emerald-500/20 ring-1 ring-emerald-400/40" : "border-white/15 bg-neutral-800/60 hover:border-white/30"}`}
                    data-testid={`signup-plan-${p.key}`}
                  >
                    <div className="font-medium text-sm text-white">{p.label}</div>
                    <div className="font-display text-xl mt-1 text-white">R$ {Number(p.price || 0).toFixed(2).replace(".",",")}<span className="text-xs text-neutral-400">/mês</span></div>
                    {p.description && <div className="text-[11px] text-neutral-300 mt-1">{p.description}</div>}
                    {!p.description && p.highlight && <div className="text-[11px] text-emerald-400 mt-1 font-medium">Mais popular</div>}
                  </button>
                ))}
                {plans.length === 0 && <div className="text-xs text-neutral-400 text-center py-4">Carregando planos…</div>}
              </div>
            </div>

            <div>
              <Label>Como quer começar?</Label>
              <div className="grid gap-2 mt-2">
                <button type="button" onClick={()=>upd("payment_mode", "trial")}
                  className={`p-3 rounded-lg border text-left transition flex items-start gap-2 ${form.payment_mode === "trial" ? "border-emerald-400 bg-emerald-500/15 ring-1 ring-emerald-400/40" : "border-white/15 bg-neutral-800/60 hover:border-white/30"}`}
                  data-testid="signup-mode-trial">
                  <Gift size={16} className="mt-0.5 text-emerald-400 flex-shrink-0"/>
                  <div>
                    <div className="font-medium text-sm text-white">7 dias grátis</div>
                    <div className="text-[11px] text-neutral-300">Sem cartão. Ao final você renova por PIX ou cartão.</div>
                  </div>
                </button>
                <button type="button" onClick={()=>upd("payment_mode", "card")}
                  className={`p-3 rounded-lg border text-left transition flex items-start gap-2 ${form.payment_mode === "card" ? "border-[#8b85ff] bg-[#635BFF]/20 ring-1 ring-[#8b85ff]/40" : "border-white/15 bg-neutral-800/60 hover:border-white/30"}`}
                  data-testid="signup-mode-card">
                  <CreditCard size={16} className="mt-0.5 text-[#a29dff] flex-shrink-0"/>
                  <div>
                    <div className="font-medium text-sm text-white">Assinar agora — cartão recorrente</div>
                    <div className="text-[11px] text-neutral-300">Cobrança automática mensal via Stripe. Acesso imediato.</div>
                  </div>
                </button>
                {pixEnabled && (
                <button type="button" onClick={()=>upd("payment_mode", "pix")}
                  className={`p-3 rounded-lg border text-left transition flex items-start gap-2 ${form.payment_mode === "pix" ? "border-indigo-400 bg-indigo-500/15 ring-1 ring-indigo-400/40" : "border-white/15 bg-neutral-800/60 hover:border-white/30"}`}
                  data-testid="signup-mode-pix">
                  <CreditCard size={16} className="mt-0.5 text-indigo-300 flex-shrink-0"/>
                  <div>
                    <div className="font-medium text-sm text-white">Pagar 1 mês por PIX</div>
                    <div className="text-[11px] text-neutral-300">Sem recorrência. Ao final você renova manualmente.</div>
                  </div>
                </button>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={()=>setStep(1)} className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/10">Voltar</Button>
              <Button onClick={submit} disabled={busy} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="signup-submit-btn">
                {busy ? <Loader2 size={14} className="animate-spin"/> :
                  form.payment_mode === "trial" ? "Começar teste" : `Pagar ${selectedPlan.label}`}
              </Button>
            </div>
            <p className="text-[10px] text-center text-neutral-400 flex items-center justify-center gap-1">
              <ShieldCheck size={10}/> Pagamento seguro via Stripe. Cancelamento a qualquer momento.
            </p>
          </div>
        )}

        {/* CTA "Falar com suporte" — visível nos dois passos, uniforme com o design escuro. */}
        {supportWhatsapp && (
          <div className="mt-6 pt-5 border-t border-white/10">
            <a
              href={`https://wa.me/${(supportWhatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent("Olá! Estou no cadastro do Gestão Expert e preciso de ajuda.")}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 text-sm font-medium transition"
              data-testid="signup-support-btn"
            >
              <MessageCircle size={16}/> Falar com suporte
            </a>
            <p className="text-[10px] text-center text-neutral-500 mt-2">Tire dúvidas rápidas com nosso time via WhatsApp.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
